/**
 * DictionaryItemFormModal — F12.N T5.3 (Settings → Słowniki → detail).
 *
 * Modal create/edit dla wartości słownika (item).
 * - `mode='create'` → wszystkie pola edytowalne
 * - `mode='edit'` → code readonly (immutable, żeby nie zerwać powiązań z istniejącymi rekordami)
 *
 * Walidacja:
 * - code: [a-z0-9][a-z0-9_-]*, 1-100 znaków
 * - label: 1-200 znaków
 * - sortOrder: int (opcjonalny; auto-max+10 jeśli pusty przy create)
 * - color: hex #rrggbb (opcjonalny)
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';

export interface DictionaryItemFormValues {
  code: string;
  label: string;
  sortOrder?: number;
  color?: string;
  /**
   * Opcjonalna wartość liczbowa pozycji jako string dziesiętny (np. stawka VAT
   * „23"). `null` czyści, pominięcie nie tyka. Trzymamy jako string (nie number),
   * bo kolumna to `numeric(18,6)` — number gubiłby precyzję na dużych wartościach.
   */
  value?: string | null;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: DictionaryItemFormValues;
  saving?: boolean;
  /**
   * Błędy zwrócone przez ostatnią próbę zapisu (np. 409 kolizja kodu), pokazywane
   * inline przy odpowiednim polu — analogicznie do `GlobalDictionaryItemFormModal`.
   * Caller ustawia nowy obiekt (nawet o tej samej treści) przy każdej próbie, żeby
   * `useEffect` poniżej zawsze wykrył zmianę i odświeżył widoczny błąd.
   */
  serverErrors?: Record<string, string>;
  /**
   * Czy pole „wartość liczbowa" ma sens dla tego słownika. `false` dla słowników
   * globalnych — ich pozycje (globalne / rozszerzenia tenanta) nie mają kolumny `value`,
   * więc backend cicho ją ignoruje; edytowalne pole wprowadzałoby w błąd (user wpisuje
   * wartość, która znika). Domyślnie `true` (słowniki lokalne, np. stawki VAT).
   */
  valueApplicable?: boolean;
  onSubmit: (values: DictionaryItemFormValues) => void;
  onClose: () => void;
}

const CODE_REGEX = /^[a-z0-9][a-z0-9_-]*$/i;
const COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;
// Regex 1:1 zwierciadło backendowego DICTIONARY_ITEM_VALUE_SCHEMA (numeric(18,6) → maks.
// 12 cyfr całości + 6 po przecinku, bez notacji wykładniczej) — walidacja przy wpisywaniu,
// żeby błąd wyłapać lokalnie, a nie dopiero na 400 z API.
const VALUE_REGEX = /^-?\d{1,12}(\.\d{1,6})?$/;

/**
 * Przycięcie zer końcowych do WYŚWIETLENIA w polu edycji. Postgres zwraca `numeric(18,6)`
 * zawsze dopełnione do skali (seedowana stawka VAT „23" wraca jako „23.000000") — user ma
 * widzieć „23", nie „23.000000". Operacja czysto stringowa (zero utraty precyzji na dużych
 * wartościach). Zapis „23" i „23.000000" backend interpretuje identycznie.
 */
function stripTrailingZeros(v: string): string {
  if (!v.includes('.')) return v;
  return v.replace(/0+$/, '').replace(/\.$/, '');
}

export function DictionaryItemFormModal({
  open,
  mode,
  initialValues,
  saving,
  serverErrors,
  valueApplicable = true,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialValues.code);
  const [label, setLabel] = useState(initialValues.label);
  const [sortOrderStr, setSortOrderStr] = useState(
    initialValues.sortOrder !== undefined ? String(initialValues.sortOrder) : '',
  );
  const [color, setColor] = useState(initialValues.color ?? '');
  const [valueStr, setValueStr] = useState(stripTrailingZeros(initialValues.value ?? ''));
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setCode(initialValues.code);
      setLabel(initialValues.label);
      setSortOrderStr(initialValues.sortOrder !== undefined ? String(initialValues.sortOrder) : '');
      setColor(initialValues.color ?? '');
      setValueStr(stripTrailingZeros(initialValues.value ?? ''));
      setErrors({});
    }
  }, [open, initialValues]);

  useEffect(() => {
    if (serverErrors) {
      setErrors((prev) => ({ ...prev, ...serverErrors }));
    }
  }, [serverErrors]);

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (mode === 'create') {
      if (!code) next.code = t('tenant.dictionaries.itemForm.errors.codeRequired');
      else if (!CODE_REGEX.test(code))
        next.code = t('tenant.dictionaries.itemForm.errors.codeInvalid');
      else if (code.length > 100) next.code = t('tenant.dictionaries.itemForm.errors.codeLength');
    }
    if (!label.trim()) next.label = t('tenant.dictionaries.itemForm.errors.labelRequired');
    else if (label.length > 200) next.label = t('tenant.dictionaries.itemForm.errors.labelLength');
    if (sortOrderStr && Number.isNaN(parseInt(sortOrderStr, 10)))
      next.sortOrder = t('tenant.dictionaries.itemForm.errors.sortOrderInvalid');
    if (color && !COLOR_REGEX.test(color))
      next.color = t('tenant.dictionaries.itemForm.errors.colorInvalid');
    const trimmedValue = valueStr.trim();
    if (trimmedValue && !VALUE_REGEX.test(trimmedValue.replace(',', '.')))
      next.value = t('tenant.dictionaries.itemForm.errors.valueInvalid');
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    const sortOrder = sortOrderStr ? parseInt(sortOrderStr, 10) : undefined;
    const trimmedValue = valueStr.trim();
    onSubmit({
      code: code.trim(),
      label: label.trim(),
      ...(sortOrder !== undefined ? { sortOrder } : {}),
      ...(color.trim() ? { color: color.trim() } : {}),
      // Zawsze przekazujemy `value`: niepuste → string (przecinek dziesiętny → kropka,
      // bo backend oczekuje kropki), puste → `null` (czyści w trybie edycji). Kolumna
      // generyczna, więc pole jest w każdym słowniku, nie tylko VAT.
      value: trimmedValue ? trimmedValue.replace(',', '.') : null,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        mode === 'create'
          ? t('tenant.dictionaries.itemForm.titleCreate')
          : t('tenant.dictionaries.itemForm.titleEdit')
      }
      size="sm"
      testId="dictionary-item-form-modal"
      // W trakcie zapisu tło i Esc nie zamykają okna („Anuluj" jest wyłączone; X w nagłówku zostaje).
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="dictionary-item-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField
          label={t('tenant.dictionaries.itemForm.code')}
          error={errors.code}
          hint={t('tenant.dictionaries.itemForm.codeHint')}
        >
          <Input
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setErrors((prev) => ({ ...prev, code: '' }));
            }}
            disabled={mode === 'edit'}
            placeholder="np. delivery"
            data-testid="dictionary-item-form-code"
          />
        </FormField>
        <FormField label={t('tenant.dictionaries.itemForm.label')} error={errors.label}>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="np. Adres dostawy"
            data-testid="dictionary-item-form-label"
          />
        </FormField>
        <FormField
          label={t('tenant.dictionaries.itemForm.sortOrder')}
          error={errors.sortOrder}
          hint={t('tenant.dictionaries.itemForm.sortOrderHint')}
        >
          <Input
            type="number"
            value={sortOrderStr}
            onChange={(e) => setSortOrderStr(e.target.value)}
            data-testid="dictionary-item-form-sort"
          />
        </FormField>
        {valueApplicable && (
          <FormField
            label={t('tenant.dictionaries.itemForm.value')}
            error={errors.value}
            hint={t('tenant.dictionaries.itemForm.valueHint')}
          >
            <Input
              value={valueStr}
              onChange={(e) => {
                setValueStr(e.target.value);
                setErrors((prev) => ({ ...prev, value: '' }));
              }}
              inputMode="decimal"
              placeholder={t('tenant.dictionaries.itemForm.valuePlaceholder')}
              data-testid="dictionary-item-form-value"
            />
          </FormField>
        )}
        <FormField
          label={t('tenant.dictionaries.itemForm.color')}
          error={errors.color}
          hint={t('tenant.dictionaries.itemForm.colorHint')}
        >
          <div className="flex items-center gap-2">
            <Input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#3B82F6"
              data-testid="dictionary-item-form-color"
            />
            <input
              type="color"
              value={COLOR_REGEX.test(color) ? color : '#3B82F6'}
              onChange={(e) => setColor(e.target.value.toUpperCase())}
              className="h-9 w-12 cursor-pointer rounded border border-border-primary"
              aria-label={t('tenant.dictionaries.itemForm.colorPicker')}
            />
          </div>
        </FormField>
      </div>
    </Modal>
  );
}
